import { api } from './api';
import { toast } from 'sonner';

// Bookmark content type mapping
export const getContentBookmarkType = (contentType) => {
  // Always return 'content' as bookmark_type
  return 'content';
};

export const getContentCategory = (contentType) => {
  // Return the actual content type as category
  switch (contentType) {
    case 'video':
      return 'video';
    case 'audio':
      return 'audio';
    case 'file':
      return 'document';
    case 'quiz':
      return 'quiz';
    case 'urls':
      return 'link';
    case 'scorm':
      return 'scorm';
    default:
      return 'file';
  }
};

// Create bookmark
export const createBookmark = async (contentId, contentType, contentName = null) => {
  try {
    const bookmarkData = {
      bookmark_type: getContentBookmarkType(contentType),
      bookmark_id: contentId,
      category: getContentCategory(contentType)
    };
    
    const response = await api.createBookmark(bookmarkData);
    
    if (contentName) {
      toast.success(`${contentName} bookmarked`);
    } else {
      toast.success('Bookmark added');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error creating bookmark:', error);
    if (error.response?.status === 409) {
      toast.error('Bookmark already exists');
    } else {
      toast.error('Failed to add bookmark');
    }
    throw error;
  }
};

// Remove bookmark
export const removeBookmark = async (bookmarkId, contentName = null) => {
  try {
    await api.deleteBookmark(bookmarkId);
    
    if (contentName) {
      toast.success(`${contentName} bookmark removed`);
    } else {
      toast.success('Bookmark removed');
    }
    
    return true;
  } catch (error) {
    console.error('Error removing bookmark:', error);
    toast.error('Failed to remove bookmark');
    throw error;
  }
};

// Get all bookmarks for current user with content details
export const getBookmarks = async () => {
  try {
    const response = await api.getBookmarks();
    const bookmarks = Array.isArray(response?.data) ? response.data : [];
    
    // Fetch content details for each bookmark
    const bookmarksWithDetails = await Promise.all(
      bookmarks.map(async (bookmark) => {
        try {
          let contentDetails = null;
          
          if (bookmark.bookmark_type === 'content') {
            // Fetch from content table
            const contentResponse = await api.getContent(bookmark.bookmark_id);
            contentDetails = contentResponse?.data || contentResponse;
          } else if (bookmark.bookmark_type === 'question') {
            // Fetch from questions table
            const questionResponse = await api.getQuestion(bookmark.bookmark_id);
            contentDetails = questionResponse?.data || questionResponse;
          }
          
          return {
            ...bookmark,
            content_name: contentDetails?.title || contentDetails?.name || contentDetails?.question_text || `Content ${bookmark.bookmark_id}`,
            description: contentDetails?.description || contentDetails?.content || contentDetails?.question_text || `Content ID: ${bookmark.bookmark_id}`,
            content_details: contentDetails
          };
        } catch (error) {
          console.error(`Error fetching details for bookmark ${bookmark.id}:`, error);
          return {
            ...bookmark,
            content_name: `${bookmark.category.charAt(0).toUpperCase() + bookmark.category.slice(1)} ${bookmark.bookmark_id}`,
            description: `Content ID: ${bookmark.bookmark_id}`,
            content_details: null
          };
        }
      })
    );
    
    return bookmarksWithDetails;
  } catch (error) {
    console.error('Error fetching bookmarks:', error);
    toast.error('Failed to fetch bookmarks');
    throw error;
  }
};

// Toggle bookmark (add if not exists, remove if exists)
export const toggleBookmark = async (contentId, contentType, contentName = null) => {
  try {
    // Get current bookmarks to check if already bookmarked
    const bookmarks = await getBookmarks();
    const bookmarksArray = Array.isArray(bookmarks) ? bookmarks : [];
    const existingBookmark = bookmarksArray.find(b => b.bookmark_id === contentId);
    
    if (existingBookmark) {
      // Remove bookmark
      await removeBookmark(existingBookmark.id, contentName);
      return { action: 'removed', bookmarkId: existingBookmark.id };
    } else {
      // Add bookmark
      const result = await createBookmark(contentId, contentType, contentName);
      return { action: 'added', bookmark: result };
    }
  } catch (error) {
    console.error('Error toggling bookmark:', error);
    throw error;
  }
};

// Get bookmarked content IDs
export const getBookmarkedContentIds = async () => {
  try {
    const bookmarks = await getBookmarks();
    const bookmarksArray = Array.isArray(bookmarks) ? bookmarks : [];
    return new Set(bookmarksArray.map(b => b.bookmark_id));
  } catch (error) {
    console.error('Error getting bookmarked content IDs:', error);
    return new Set();
  }
};

// Check if content is bookmarked
export const isContentBookmarked = async (contentId) => {
  try {
    const bookmarks = await getBookmarks();
    const bookmarksArray = Array.isArray(bookmarks) ? bookmarks : [];
    return bookmarksArray.some(b => b.bookmark_id === contentId);
  } catch (error) {
    console.error('Error checking bookmark status:', error);
    return false;
  }
};

// Get bookmarks by category
export const getBookmarksByCategory = async (category) => {
  try {
    const bookmarks = await getBookmarks();
    const bookmarksArray = Array.isArray(bookmarks) ? bookmarks : [];
    return bookmarksArray.filter(b => b.category === category);
  } catch (error) {
    console.error('Error getting bookmarks by category:', error);
    return [];
  }
};

export default {
  createBookmark,
  removeBookmark,
  getBookmarks,
  toggleBookmark,
  getBookmarkedContentIds,
  isContentBookmarked,
  getBookmarksByCategory,
  getContentBookmarkType,
  getContentCategory
};
